<script setup lang="ts">
import { ref, computed } from 'vue';
import { CustomerMgmtTypes, useEditUser } from '@ilteducation/customer-mgmt';
import pMap from 'p-map';

import Modal from '../Modal.vue';
import useToast from '../../composables/useToast';
import { useLocale } from '../../composables/useLocale';
import CreateMultipleLoginCards from './CreateMultipleLoginCards.vue';
import useModal, { modals } from '../../composables/useModal';
import { generateQrToken, generateRandomPassword } from '../../utils';
import { useCreateLoginCards } from './useCreateLoginCards';

const { t } = useLocale();
const { toast } = useToast();
const { updateUser } = useEditUser();

type Props = {
  createQrCodeSelected?: boolean;
  createPasswordSelected?: boolean;
};

const props = defineProps<Props>();

const { isOpen, close, context: modalContext } = useModal(modals.CREATE_MULTIPLE_LOGIN_CARDS);
const { usersToCreateLoginCard, usersNotAllowedToUpdate, isUsersLoading, reset } = useCreateLoginCards();
const countOfUsersNotAllowedToUpdate = computed(() =>
  usersNotAllowedToUpdate.value ? Object.values(usersNotAllowedToUpdate.value).reduce((a, b) => a + b.length, 0) : 0,
);

const loginCards = ref<typeof CreateMultipleLoginCards>();

const isUsersUpdating = ref(false);
const isUsersUpdated = ref(false);
const isDownloaded = ref(false);
const progress = ref(0);
const isWorking = ref(false);

const createPassword = ref(props.createPasswordSelected ?? false);
const createQrCode = ref(props.createQrCodeSelected ?? false);

const getUsername = (user: CustomerMgmtTypes.GetUserResponse | CustomerMgmtTypes.SearchUserOutput) => {
  if ('username' in user) {
    return user.username;
  }

  if ('userName' in user) {
    return user.userName;
  }

  return '';
};

const getFullName = (user: CustomerMgmtTypes.GetUserResponse | CustomerMgmtTypes.SearchUserOutput) => {
  if ('fullname' in user) {
    return user.fullname;
  }

  if ('name' in user) {
    return user.name;
  }

  return '';
};

const usersForLoginCard = computed(() => {
  return usersToCreateLoginCard.value.map((u) => ({
    uid: u.uid,
    name: getFullName(u),
    userName: getUsername(u),
    password: createPassword.value ? generateRandomPassword() : undefined,
    qrToken: createQrCode.value ? generateQrToken() : undefined,
  }));
});

const updateUsersLoginInfo = async () => {
  isUsersUpdating.value = true;

  let index = 0;
  await pMap(
    usersForLoginCard.value,
    async (u) => {
      await updateUserLoginInfo(
        u.uid,
        createQrCode.value ? u.qrToken : undefined,
        createPassword.value ? u.password : undefined,
      );
      progress.value = Math.round(((index + 1) / usersForLoginCard.value.length) * 100);
      index++;
    },
    { concurrency: 5 },
  );

  isUsersUpdating.value = false;
  isUsersUpdated.value = true;
  return true;
};

const userFailedToUpdate = ref([] as string[]);
const updateUserLoginInfo = async (userUid: string, qrToken?: string, password?: string) => {
  const success = await updateUser(userUid, { qrToken, password });
  if (!success) {
    userFailedToUpdate.value.push(userUid);
  }

  return success;
};

const downloadLoginCards = async () => {
  if (!loginCards.value) {
    return false;
  }

  return loginCards.value.downloadLoginCard();
};

const onDownloaded = () => {
  toast(t('lcp.login_cards_created_successfully'), 'success');
  isDownloaded.value = true;
};

const action = async () => {
  isWorking.value = true;
  try {
    if (!isUsersUpdated.value) {
      await updateUsersLoginInfo();
    }
    await downloadLoginCards();
  } catch (error) {
    console.error('Failed to save the login cards', error);
  } finally {
    isWorking.value = false;
  }
};

const closeAndReset = (force = false) => {
  if (!force && (isWorking.value || isDownloaded.value)) {
    return;
  }

  isUsersUpdating.value = false;
  isUsersUpdated.value = false;
  isDownloaded.value = false;
  isWorking.value = false;
  progress.value = 0;
  createPassword.value = props.createPasswordSelected ?? false;
  createQrCode.value = props.createQrCodeSelected ?? false;
  reset();
  close();
};
</script>

<template>
  <Modal
    :name="modals.CREATE_MULTIPLE_LOGIN_CARDS"
    :title="t('lcp.create_new_login_cards')"
    :is-open="isOpen"
    :loading="isUsersLoading || isWorking"
    :action="action"
    action-append-icon="mdi-download"
    :action-disabled="
      isUsersLoading || isUsersUpdating || (!createQrCode && !createPassword) || usersForLoginCard.length === 0
    "
    :action-label="isDownloaded ? t('lcp.download_login_cards') : t('lcp.create_and_download')"
    :cancel-action-label="isDownloaded ? t('lcp.done') : t('lcp.cancel')"
    :cancel-action="() => (isDownloaded ? closeAndReset(true) : closeAndReset(true))"
    @close="closeAndReset"
    :disable-auto-close="isWorking || isDownloaded"
  >
    <v-row no-gutterss>
      <v-col cols="12">
        <v-alert type="warning" variant="outlined" border="start">
          {{ t('lcp.create_login_card_warning') }}
        </v-alert>
      </v-col>
      <v-col cols="12">
        <v-skeleton-loader v-if="isUsersLoading" type="sentences" />
        <v-expansion-panels v-else variant="accordion" tile static :elevation="0" :loading="true">
          <v-expansion-panel v-if="usersForLoginCard" expand-icon="" collapse-icon="">
            <template #title>
              <p>
                {{ t('lcp.creating_login_cards_for_users', { count: usersForLoginCard.length }) }}
              </p>
            </template>
          </v-expansion-panel>
          <v-expansion-panel
            v-if="usersNotAllowedToUpdate && Object.keys(usersNotAllowedToUpdate).length > 0"
            class="text-error"
          >
            <template #title>
              <p>
                {{
                  t('lcp.not_creating_login_cards_for_users', {
                    count: countOfUsersNotAllowedToUpdate,
                  })
                }}
              </p>
            </template>
            <template #text>
              <p v-for="(users, reason) in usersNotAllowedToUpdate" :key="reason" class="text-caption">
                {{ users.length }} {{ t(`lcp.permissions.${reason}`) }}
              </p>
            </template>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-col>
      <v-col cols="12">
        <p>{{ t('lcp.select_login_credentials') }}</p>
        <v-checkbox
          :disabled="isUsersUpdated || usersForLoginCard.length === 0"
          v-model="createQrCode"
          :label="t('lcp.qr_code')"
          hide-details
        ></v-checkbox>
        <v-checkbox
          :disabled="isUsersUpdated || usersForLoginCard.length === 0"
          v-model="createPassword"
          :label="t('lcp.password')"
          hide-details
        ></v-checkbox>
        <span cols v-if="!createQrCode && !createPassword">
          <v-alert type="info" variant="outlined" border="start">
            {{ t('lcp.select_at_least_one_option') }}
          </v-alert>
        </span>
      </v-col>
      <v-col cols="12" v-if="progress > 0">
        <v-progress-linear
          :model-value="isDownloaded ? progress : progress - 5"
          color="primary"
          height="8"
          class="mb-4"
        ></v-progress-linear>
      </v-col>
      <v-col cols="12" v-if="userFailedToUpdate.length > 0 && isDownloaded">
        <v-alert type="error" variant="outlined" border="start">
          {{ userFailedToUpdate.length }}: {{ t('lcp.failed_to_create_login_card') }}
        </v-alert>
      </v-col>
    </v-row>
    <div style="display: none">
      <CreateMultipleLoginCards
        ref="loginCards"
        v-if="usersForLoginCard && usersForLoginCard.length > 0"
        :users="usersForLoginCard"
        :fileName="modalContext.fileName"
        @downloaded="onDownloaded"
      />
    </div>
  </Modal>
</template>
